import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import { PageLayout } from "./components/layout/PageLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import HomePage from "./pages/HomePage";
import ObligoException from "./pages/obligoException";
import PermissionsPage from "./pages/Permissions";
import CheckInvoicesPage from "./pages/CheckInvoices";
import PurchaseInvoicePage from "./pages/purchaseInvoices/purchaseInvoicePage";
import TimeLinePage from "./pages/TimeLine";
import ViewInvoice from "./pages/purchaseInvoices/viewInvoice/viewInvoice";
import ViewAttachments from "./pages/purchaseInvoices/viewAttachments/viewAttachments";
import InvoiceRedirect from "./components/purchaseInvoices/redirect/InvoiceRedirect";
import EldanPrinterPage from "./pages/EldanPrinter";
import QrCodeDeviceMappingPage from "./pages/QrCodeDeviceMapping";
import AgentComplaintsPage from "./pages/AgentsComplain/AgentComplaints";
import AgentComplaintForm from "./pages/AgentsComplain/AgentComplaintForm";
import LibertyMedicalConfirmPage from "./pages/LibertyMedicalConfirm/LibertyMedicalConfirmPage";
import SalCommitteePage from "./pages/SalCommittee/SalCommitteePage";
import ManagersDashboardLayout from "./pages/ManagersDashboard/ManagersDashboardLayout";
import ManagersDashboardHome from "./pages/ManagersDashboard/ManagersDashboardHome";
import ManagerDashboardPermissionsPage from "./pages/ManagersDashboard/Permissions/ManagerDashboardPermissionsPage";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CheckedInvoicesTable from "./components/CheckedInvoices/CheckedInvoicesTable";
import InvoicesAndFioesUpload from "./components/UploadInvoicesAI/InvoicesAndFioesUpload";

import PriceListUpload from "./components/PriceListUpdate/PriceListUpload";
import ApproveLinesPage from "./pages/ApproveLines";
import ApproveLinesStatisticsPage from "./pages/ApproveLinesStatisticsPage";
import TradeDelayPage from "./pages/TradeDelay";
import SalesPromotionsPage from "./pages/SalesPromotions";
import Form161Page from "./pages/Form161/Form161Page";
import { UserProvider } from "./contexts/UserContext";
import { AgentProvider } from "./contexts/AgentContext";
import { BranchesProvider } from "./contexts/BranchesContext";
import { TransactionsTypeProvider } from "./contexts/TransactionsTypeContext";
import { ToastProvider } from "./contexts/ToastContext";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import usePostLoginRedirect from "./hooks/usePostLoginRedirect";
import ClarificationStandalonePage from "./pages/clarification/ClarificationStandalonePage";
import { SnackbarProvider } from "notistack";

const theme = createTheme({
  direction: "rtl",
});

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

// Create rtl theme
const rtlTheme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
  },
});

const appStyle = {
  backgroundColor: "#f8f8f8",
  minHeight: "100vh",
  width: "100%",
};

const AppContent = () => {
  usePostLoginRedirect();

  return (
    <Routes>
      {/* Standalone pages — no sidebar/header */}
      <Route path="/clarification/:clarificationId" element={<ClarificationStandalonePage />} />

      {/* All other pages inside PageLayout */}
      <Route
        path="*"
        element={
          <div className="App" dir="rtl" style={appStyle}>
            <PageLayout>
              <Routes>
                <Route path="/" element={<HomePage />} />

                <Route path="/i-v/:invoiceId" element={<InvoiceRedirect />} />

                <Route
                  path="/liberty-medical-invoice-confirm"
                  element={
                    <ProtectedRoute requiredPermission="LibertyMedicalPage">
                      <LibertyMedicalConfirmPage />
                    </ProtectedRoute>
                  }
                />

                <Route path="/i-r/:fileName" element={<ViewInvoice />} />

                <Route path="/v-a/:invoiceId" element={<ViewAttachments />} />

                <Route
                  path="/Permissions"
                  element={
                    <ProtectedRoute requiredPermission="PermissionsPage">
                      <PermissionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/SalesPromotions/:orderId?"
                  element={
                    <ProtectedRoute requiredPermission="SalesPromotionsPage">
                      <SalesPromotionsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/PurchaseInvoice"
                  element={
                    <ProtectedRoute requiredPermission="PurchaseInvoicePage">
                      <PurchaseInvoicePage />
                    </ProtectedRoute>
                  }
                />

                {/* CheckInvoices routes with proper nesting */}
                <Route
                  path="/CheckInvoices"
                  element={
                    <ProtectedRoute requiredPermission="CheckInvoicesPage">
                      <CheckInvoicesPage />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="checked" replace />} />
                  <Route path="checked" element={<CheckedInvoicesTable />} />
                  <Route path="upload" element={<InvoicesAndFioesUpload />} />
                  <Route path="pricelist" element={<PriceListUpload />} />
                </Route>

                <Route
                  path="/ManagersDashboard"
                  element={
                    <ProtectedRoute requiredPermission="ManagersDashboardPage">
                      <ManagersDashboardLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<ManagersDashboardHome />} />
                  <Route path="permissions" element={<ManagerDashboardPermissionsPage />} />
                </Route>

                <Route
                  path="/ApproveLines"
                  element={
                    <ProtectedRoute requiredPermission="AproveLinesPage">
                      <ApproveLinesPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ApproveLinesStatistics"
                  element={
                    <ProtectedRoute requiredPermission="AproveLinesPage">
                      <ApproveLinesStatisticsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/TradeDelay"
                  element={
                    <ProtectedRoute requiredPermission="TradeDelayPage">
                      <TradeDelayPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/ObligoException"
                  element={
                    <ProtectedRoute requiredPermission="ObligoExceptionPage">
                      <ObligoException />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path=":custId"
                    element={
                      <ProtectedRoute requiredPermission="ObligoExceptionPage">
                        <ObligoException />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route
                  path="/TimeLine"
                  element={
                    <ProtectedRoute requiredPermission="TimeLinePage">
                      <TimeLinePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Form161"
                  element={
                    <ProtectedRoute requiredPermission="Form161Page">
                      <Form161Page />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/Eldanprinter"
                  element={
                    <ProtectedRoute requiredPermission="EldanPrinterPage">
                      <EldanPrinterPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/QrCodeDeviceMapping"
                  element={
                    <ProtectedRoute requiredPermission="QrCodeDeviceMappingPage">
                      <QrCodeDeviceMappingPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/agent-complaints"
                  element={
                    <ProtectedRoute requiredPermission="AgentComplaintsPage">
                      <AgentComplaintsPage />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/agent-complaints/new"
                  element={
                    <ProtectedRoute requiredPermission="AgentComplaintsPage">
                      <AgentComplaintForm />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/sal-committee"
                  element={
                    <ProtectedRoute requiredPermission="SalCommitteePage">
                      <SalCommitteePage />
                    </ProtectedRoute>
                  }
                />

              </Routes>
            </PageLayout>
          </div>
        }
      />
    </Routes>
  );
};

const App = ({ instance }) => {
  const queryClient = new QueryClient();

  return (
    <MsalProvider instance={instance}>
      <UserProvider>
        <AgentProvider>
        <BranchesProvider>
          <TransactionsTypeProvider>
            <ToastProvider>
              <PermissionsProvider>
                <CacheProvider value={cacheRtl}>
                  <ThemeProvider theme={theme}>
                    <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                      <ErrorBoundary>
                        <Worker workerUrl="/pdf.worker.js">
                          <AppContent />
                        </Worker>
                      </ErrorBoundary>
                    </SnackbarProvider>
                  </ThemeProvider>
                </CacheProvider>
              </PermissionsProvider>
            </ToastProvider>
          </TransactionsTypeProvider>
        </BranchesProvider>
        </AgentProvider>
      </UserProvider>
    </MsalProvider>
  );
};

export default App;
